import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { GlobalService } from './global.service';
import { map, catchError, debounceTime, shareReplay } from 'rxjs/operators'
import { environment } from 'src/environments/environment';
import { Subject, Observable } from 'rxjs';
import { LocalStorageService } from './local-storage.service';
import { PersonSearchService } from './person-search.service';
import { CreditActivityService } from './credit-activity.service';

const devUrl = environment.backendurl

@Injectable({
  providedIn: 'root'
})
export class TransactionService {

  constructor(
    private http: HttpClient,
    private localstorageservice: LocalStorageService,
    private personSearchService: PersonSearchService,
    private creditActivityService: CreditActivityService
  ) {
    // Credits are fetched on login, after successful credit-consuming actions, and on entering
    // a credits-gated module (CreditsRefreshGuard). CreditActivityService debounces rapid
    // notifications into a single refresh call.
    this.creditActivityService.creditConsumed$.pipe(
      debounceTime(600)
    ).subscribe(() => this.updateAllCreditsForCurrentUser().subscribe());

    // Seed credits/access synchronously from the last persisted payload so values survive page refresh.
    // This avoids null signals without a bootstrap API call; values are refreshed on login or relevant actions.
    this._credits.set(this.localstorageservice.getItem('creditsDetails'));
    this._access.set(this.localstorageservice.getItem('accessDetails'));
  }

  public fetchCurrentPlanDetailsData: Subject<any> = new Subject();

  /**
   * Latest known credits payload, shared across the whole app. Populated on login/app-bootstrap
   * and whenever CreditActivityService reports a credit-consuming action succeeded - components
   * can read `transactionService.credits()` directly (works in templates too, no async pipe
   * needed) instead of each calling fetchcurrentplandetails()/reading localStorage themselves.
   */
  private _credits = signal<any>(null);
  public readonly credits = this._credits.asReadonly();

  /**
   * Latest known access/feature-flag payload, shared across the whole app the same way as
   * `credits` above.
   */
  private _access = signal<any>(null);
  public readonly access = this._access.asReadonly();

  /**
   * Refresh all plan & credits details of current user
   * @description
   * Generic function to fetch current user's plan details incl. credits and also update the header & stores with latest details
   * Call this method to automatically handle all plan & credits-related data refresh.
   * Callers: signup.component.ts right after login; this service's own constructor subscription
   * to CreditActivityService.creditConsumed$ (after a reveal-contact action succeeds);
   * CreditsRefreshGuard on credits-gated routes; and any pre-reveal credit check that needs the
   * current balance before deciding whether to proceed. No caching - every call hits the API.
   * @returns Observable resolving to object containing user's latest plan details
   */
  updateAllCreditsForCurrentUser() {
    return new Observable(observer => {
      const currentUserNumber = this.localstorageservice.getItem('usernumber');
      if (!currentUserNumber) {
        observer.next({});
        observer.complete();
        return;
      }

      this.fetchcurrentplandetails(currentUserNumber).pipe(
        map((res: any) => {
          if (res.status === 'success') {
            const subscriptionDetails: any = res?.data || {};

            subscriptionDetails.balancecontactenrichmentcredits = res.data.balancecontactenrichmentcredits;
            subscriptionDetails.balancebasiccontactenrichmentcredits = res.data.balancebasiccontactenrichmentcredits;

            if (subscriptionDetails.isteamadmin) {
              // Here the logged in person is a Team Admin
              subscriptionDetails.balancejdparsingcredits = res.data.pooledbalancejdparsingcredits;
              subscriptionDetails.balancesmartemailcredits = res.data.pooledbalancesmartemailcredits;
              subscriptionDetails.balancetalentpipelinecredits = res.data.pooledbalancetalentpipelinecredits;
              subscriptionDetails.balancebulkgrabbingcredits = res.data.pooledbalancebulkgrabbingcredits;
              subscriptionDetails.cepercentage = ((subscriptionDetails.balancecontactenrichmentcredits / subscriptionDetails.contactenrichmentcredits) * 100).toFixed(2)

              this.localstorageservice.setItem('isteamAdmin', true);
            } else {
              if (subscriptionDetails.teamadmindetails) {
                // Here the logged in person is a Team Member
                subscriptionDetails.balancejdparsingcredits = res.data.teamadmindetails.adminbalancejdparsingcredits;
                subscriptionDetails.balancesmartemailcredits = res.data.teamadmindetails.adminbalancesmartemailcredits;
                subscriptionDetails.balancebulkgrabbingcredits = res.data.teamadmindetails.adminbalancebulkgrabbingcredits;
                subscriptionDetails.balancetalentpipelinecredits = res.data.teamadmindetails.adminbalancetalentpipelinecredits;
                subscriptionDetails.cepercentage = ((subscriptionDetails.balancecontactenrichmentcredits / subscriptionDetails.reservedcontactcreditscount) * 100).toFixed(2)
                subscriptionDetails.bcepercentage = ((subscriptionDetails.balancebasiccontactenrichmentcredits / subscriptionDetails.reservedbasiccontactcreditscount) * 100).toFixed(2)
                subscriptionDetails.contactenrichmentcredits = subscriptionDetails.reservedcontactcreditscount;
                subscriptionDetails.basiccontactenrichmentcredits = subscriptionDetails.reservedbasiccontactcreditscount;

                this.localstorageservice.setItem('isteamMember', true);
              } else {
                // Here the logged in person is a normal (who does not belong to any team)
                subscriptionDetails.balancejdparsingcredits = res.data.balancejdparsingcredits;
                subscriptionDetails.balancesmartemailcredits = res.data.balancesmartemailcredits;
                subscriptionDetails.balancetalentpipelinecredits = res.data.balancetalentpipelinecredits;
                subscriptionDetails.balancebulkgrabbingcredits = res.data.balancebulkgrabbingcredits;
                subscriptionDetails.cepercentage = ((subscriptionDetails.balancecontactenrichmentcredits / subscriptionDetails.contactenrichmentcredits) * 100).toFixed(2)

                this.localstorageservice.setItem('isteamMember', null);
              }
            }

            subscriptionDetails.jdpercentage = ((subscriptionDetails.balancejdparsingcredits / subscriptionDetails.jdparsingcredits) * 100).toFixed(2);
            subscriptionDetails.sepercentage = ((subscriptionDetails.balancesmartemailcredits / subscriptionDetails.smartemailcredits) * 100).toFixed(2);
            subscriptionDetails.grabpercentage = ((subscriptionDetails.balancebulkgrabbingcredits / subscriptionDetails.bulkgrabbingcredits) * 100).toFixed(2);
            subscriptionDetails.tppercentage = ((subscriptionDetails.balancetalentpipelinecredits / subscriptionDetails.talentpipelinecredits) * 100).toFixed(2);
            subscriptionDetails.overallpercentage = ((res.data.totalbalancecreditswithentireteam / subscriptionDetails.contactenrichmentcredits) * 100).toFixed(2);
            subscriptionDetails.overallbasicpercentage = ((res.data.totalbalancebasiccreditswithentireteam / subscriptionDetails.basiccontactenrichmentcredits) * 100).toFixed(2);
            subscriptionDetails.overallcredits = res.data.totalbalancecreditswithentireteam
            subscriptionDetails.overallbasiccredits = res.data.totalbalancebasiccreditswithentireteam

            // update in localstorage
            this.localstorageservice.setItem('paymentplanname',subscriptionDetails.paymentplanname);
            this.localstorageservice.setItem('plantype',subscriptionDetails.plantype);
            this.localstorageservice.setItem('jdcredits', subscriptionDetails.balancejdparsingcredits);
            this.localstorageservice.setItem('tpcredits', subscriptionDetails.balancetalentpipelinecredits);
            this.localstorageservice.setItem('cecredits', subscriptionDetails.balancecontactenrichmentcredits);
            this.localstorageservice.setItem('secredits', subscriptionDetails.balancesmartemailcredits);
            this.localstorageservice.setItem('grabcredits', subscriptionDetails.balancebulkgrabbingcredits);
            const userData = this.localstorageservice.getItem('userdata');
            userData['balancebasiccontactenrichmentcredits'] = subscriptionDetails.balancebasiccontactenrichmentcredits;
            this.localstorageservice.setItem('userdata', userData);

            // shared, reactive credits state - visible to any component without an API call
            this._credits.set(subscriptionDetails);
            // persisted so the signal can be re-seeded on the next page load without a fetch
            this.localstorageservice.setItem('creditsDetails', subscriptionDetails);

            // notify header to render latest data
            this.createPaymentData();

            observer.next(subscriptionDetails);
            observer.complete();
          }
        })
      ).subscribe();
    });
  }

  /**
   * Fetch and update access details for the current user
   * @description
   * Generic function to fetch current user's plan access details and update localStorage with latest access flags.
   * Handles region fallback if user is on 'europe' but lacks EU access.
   * Call this method to automatically handle all access-related data refresh.
   * @param forceRefresh bypass the shareReplay cache in fetchAccessDetails() - used by this
   * service's own background poll so a superadmin-side access change is picked up on schedule
   * instead of being served the still-cached pre-change response.
   * @returns Observable resolving to the access details data
   */
  updateAccessDetailsForCurrentUser(forceRefresh: boolean = false) {
    return new Observable(observer => {
      this.fetchAccessDetails(forceRefresh).subscribe({
        next: (res: any) => {
          if (res.status == 'success') {
            this.localstorageservice.setItem('hasEUPersonSearchAccess', res.data.haseupersonsearchaccess);
            this.localstorageservice.setItem('hasEUTalentInsightsAccess', res.data.haseutalentinsightsaccess);
            this.localstorageservice.setItem('haseuaisourcingaccess', res.data.haseuaisourcingaccess);
            // field is `hasaisourcingaccess` (no "slots") - fetchcurrentplanaccess never returns `autopilotslotsaccess`
            this.localstorageservice.setItem('autopilotslotaccess', res.data?.hasaisourcingaccess);
            this.localstorageservice.setItem('hasjoinedaisourcingwaitlist', res.data?.hasjoinedaisourcingwaitlist);
            this.localstorageservice.setItem('autopilotprojectslots', res.data?.autopilotprojectslots);
            this.localstorageservice.setItem('hasjoblistingaccess', res.data?.hasjoblistingaccess);
            this.localstorageservice.setItem('defaultContinent', res?.data?.preferredsearchcontinent);

            const currentRegion = this.localstorageservice.getItem('region');
            if (currentRegion == 'europe' && !res.data.haseupersonsearchaccess) {
              this.personSearchService.getGeoIpLocationOfUser.subscribe((response: any) => {
                const region = response.continent ? response.continent.toLowerCase() : 'asia';
                this.localstorageservice.setItem('region', region);
              });
            }
            const currentTIRegion = this.localstorageservice.getItem('talentRegion');
            if (currentTIRegion == 'europe' && !res.data.haseutalentinsightsaccess) {
              this.personSearchService.getGeoIpLocationOfUser.subscribe((response: any) => {
                const region = response.continent ? response.continent.toLowerCase() : 'asia';
                this.localstorageservice.setItem('talentRegion', region);
              });
            }

            // shared, reactive access state - visible to any component without an API call
            this._access.set(res.data);
            this.localstorageservice.setItem('accessDetails', res.data);

            observer.next(res.data);
            observer.complete();
          }
        },
        error: (err: any) => {
          console.log("error in updateAccessDetailsForCurrentUser", err);
          observer.error(err);
        }
      });
    });
  }

  /**
   * How long a fetched access payload is considered fresh before the next call re-hits the API.
   * Access flags (EU access, autopilot, job listing) change rarely, so this avoids re-fetching
   * on every single access check across the app (~10 call sites call this independently).
   */
  private readonly ACCESS_CACHE_TTL_MS = 5 * 60 * 1000;
  private accessCache$: Observable<any> | null = null;
  private accessCacheExpiresAt = 0;

  /**
   * Fetch current plan ACCESS/feature-flag data (EU access, autopilot/AI-sourcing access, job listing
   * access, contactProviders) for the logged-in user. Hits /paymentflow/fetchcurrentplanaccess.
   * The backend identifies the user from the auth token (request.user); no usernumber param is sent.
   *
   * "Smart" call site: within ACCESS_CACHE_TTL_MS, every caller (regardless of where in the app)
   * shares the same in-flight/last-completed request instead of firing a new HTTP call each time -
   * no consumer needs to change how it calls this to benefit. Pass forceRefresh=true (e.g. right
   * after a plan/access-changing action) to bypass the cache.
   * @returns Observable<any>
   */
  fetchAccessDetails(forceRefresh: boolean = false){
    const isCacheFresh = this.accessCache$ && Date.now() < this.accessCacheExpiresAt;
    if (!forceRefresh && isCacheFresh) {
      return this.accessCache$;
    }

    let url = devUrl + "/paymentflow/fetchcurrentplanaccess"
    this.accessCacheExpiresAt = Date.now() + this.ACCESS_CACHE_TTL_MS;
    this.accessCache$ = this.http.get(url).pipe(
      map(response => { return response; }),
      catchError((err) => {
        // don't keep serving a cached observable for a failed request
        this.accessCache$ = null;
        this.accessCacheExpiresAt = 0;
        return GlobalService.handleError('fetchcurrentplanaccess', url)(err);
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );
    return this.accessCache$;
  }

  /**
   * Fetch current plan CREDIT data (balances, plan identity, team/admin branching) for the logged-in user.
   * NOTE: despite the method name (kept for backward compatibility with existing callers), this hits
   * /paymentflow/fetchcurrentplancredits, not the old /paymentflow/fetchcurrentplandetails. It does not
   * return access/feature flags (EU access, autopilot, job listing) - use fetchAccessDetails() for those.
   * The backend identifies the user from the auth token (request.user), so no usernumber query param
   * is sent; the userid argument is accepted for backward compatibility with existing call sites only.
   * Always hits the API - no cache. Every call (login, post-reveal, module navigation, a fresh
   * pre-reveal credit check) needs the current balance, not a throttled one.
   */
  fetchcurrentplandetails(userid?){
    const userNumber = this.localstorageservice.getItem('usernumber');
    let url = `${devUrl}/paymentflow/fetchcurrentplancredits?usernumber=${userNumber}`;
    return this.http.get(url).pipe(
      map(response => { return response; }),
      catchError(GlobalService.handleError('fetchcurrentplancredits', url))
    );
  }


  fetchpaymenttransactionhistory(userid,startindex){
    let url = devUrl + "/paymentflow/fetchpaymenttransactionhistory?usernumber="+userid+"&startindex="+startindex
    return this.http.get(url).pipe(
      map(response => { return response; })
    , catchError(GlobalService.handleError('fetchcurrentplandetails', url)));
  }

  chaneplan(data){
    var url = devUrl + "/paymentflow/changeplanrequest"
    return this.http.post(url, data).pipe(map(response => { return response; })
      , catchError(GlobalService.handleError('chaneplan', url)));
  }

  fetchallactivepaymentplans(){
    let url = devUrl + "/paymentflow/fetchallactivepaymentplans"
    return this.http.get(url).pipe(
      map(response => { return response; })
    , catchError(GlobalService.handleError('fetchallactivepaymentplans', url)));
  }

  sendcreditexpirymail(data){
    let url = devUrl + "/paymentflow/sendcreditexpirymail"
    return this.http.post(url, data).pipe(map(response => { return response; })
      , catchError(GlobalService.handleError('chaneplan', url)));
  }

  fetchallstripeplans(){
    let url = devUrl + "/paymentflow/fetchallstripeplans"
    return this.http.get(url).pipe(map(response => { return response; })
      , catchError(GlobalService.handleError('fetchallstripeplans', url)));
  }

  createcustomer(data){
    let url = devUrl + "/stripe/createcustomer"
    return this.http.post(url,data).pipe(map(response => { return response; })
      , catchError(GlobalService.handleError('createcustomer', url)));
  }

  createcheckouturl(data){
    let url = devUrl + "/stripe/createcheckouturl"
    return this.http.post(url,data).pipe(map(response => { return response; })
      , catchError(GlobalService.handleError('createcheckouturl', url)));
  }

  verifypaymentstatus(data){
    let url = devUrl + "/stripe/verifypaymentstatus"
    return this.http.post(url,data).pipe(map(response => { return response; })
      , catchError(GlobalService.handleError('verifypaymentstatus', url)));
  }

  cancelsubscription(data){
    let url = devUrl + "/stripe/cancelsubscription"
    return this.http.post(url,data).pipe(map(response => { return response; })
      , catchError(GlobalService.handleError('verifypaymentstatus', url)));
  }

  fetchplan(data){
    let url = devUrl + "/paymentflow/fetchplan?paymentplanid=" + data
    return this.http.get(url).pipe(map(response => { return response; })
      , catchError(GlobalService.handleError('verifypaymentstatus', url)));
  }


public createPaymentData (): void {
  this.fetchCurrentPlanDetailsData.next(undefined as any);
}

public getPaymentData (): Observable<any> {
  return this.fetchCurrentPlanDetailsData.asObservable();
}

}
